import { z } from 'zod';
import { PCMP_BATCH_STATUSES } from './pcmpMis.types';
import { PCMP_CHANNELS } from './pcmpProposal.types';
import { pcmpCmpReportSchema } from './pcmpReport.types';
import {
  pcmpPageMetaSchema,
  pcmpQueuePersonSchema,
  pcmpSkippedProposalSchema,
  type PcmpQueueFilters,
} from './pcmpQueue.types';
import { pcmpRequestMineItemSchema, pcmpRequestQueueItemSchema } from './pcmpRequest.types';
import type { PcmpChannel } from './pcmpProposal.types';
import { pcmpDivisionTotalSchema, pcmpRegionTotalSchema } from './pcmpRequested.types';

// Urgent payment batches (API LB-779). Lists and drill-downs are read from
// `pcmp/requests/batches`; stage, publish and rollback live under `pcmp/urgent`.

const bucketSchema = z.object({ count: z.number(), amount: z.number() });

/** The viewer's share of a batch, by where each proposal stands. */
const bucketsShape = {
  itemCount: z.number(),
  totalAmount: z.number(),
  pending: bucketSchema,
  disbursed: bucketSchema,
  disbursementFailed: bucketSchema,
  rejected: bucketSchema,
  returned: bucketSchema,
  held: bucketSchema,
};

export const pcmpUrgentBatchSchema = z.object({
  batchId: z.coerce.number(),
  batchNo: z.number(),
  channel: z.enum(PCMP_CHANNELS).nullable(),
  status: z.enum(PCMP_BATCH_STATUSES),
  isPublished: z.boolean(),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
  createdBy: pcmpQueuePersonSchema.nullable(),
  ...bucketsShape,
  /** Every member, whatever the viewer may see of it. */
  wholeBatchItemCount: z.number(),
  rollUp: z.string(),
  /** Members matching the filters; null when none is set. */
  matchedItemCount: z.number().nullable(),
});

export type PcmpUrgentBatch = z.infer<typeof pcmpUrgentBatchSchema>;

export const pcmpUrgentBatchesSchema = z.object({
  totals: z.object({ ...bucketsShape, batches: z.number() }),
  batches: z.object({ items: z.array(pcmpUrgentBatchSchema), meta: pcmpPageMetaSchema }),
});

export type PcmpUrgentBatches = z.infer<typeof pcmpUrgentBatchesSchema>;

export const pcmpUrgentBatchHeaderSchema = z.object({
  batchId: z.coerce.number(),
  batchNo: z.number(),
  channel: z.enum(PCMP_CHANNELS).nullable(),
  status: z.enum(PCMP_BATCH_STATUSES),
  isPublished: z.boolean(),
  publishedAt: z.string().nullable(),
  wholeBatchItemCount: z.number(),
  /** True when at least one published item can be rolled back (LDS-823). */
  batchHasRollbackItems: z.boolean().optional().default(false),
});

export type PcmpUrgentBatchHeader = z.infer<typeof pcmpUrgentBatchHeaderSchema>;

/** Urgent batch item: the queue row plus per-item rollback eligibility (LDS-823). */
export const pcmpUrgentBatchItemSchema = pcmpRequestQueueItemSchema.extend({
  canRollback: z.boolean().optional().default(false),
  rollbackBlockedReason: z.string().nullable().optional().default(null),
});

export type PcmpUrgentBatchItem = z.infer<typeof pcmpUrgentBatchItemSchema>;

export const pcmpUrgentBatchProposalsSchema = z.object({
  batch: pcmpUrgentBatchHeaderSchema,
  /** True when at least one item in the batch can be rolled back (LDS-823). */
  batchHasRollbackItems: z.boolean().optional().default(false),
  proposals: z.object({ items: z.array(pcmpUrgentBatchItemSchema), meta: pcmpPageMetaSchema }),
});

export type PcmpUrgentBatchProposals = z.infer<typeof pcmpUrgentBatchProposalsSchema>;

// Stage, publish, rollback and the report.

export type PcmpUrgentStageSelection =
  | { proposalIds: number[] }
  | {
      selectAllMatching: true;
      filters: PcmpQueueFilters;
      excludedProposalIds: number[];
    };

export type PcmpUrgentStagePayload = {
  /** Add to this open (staged) batch; left out, a new batch is opened. */
  batchNo?: number | undefined;
  comment?: string | undefined;
} & PcmpUrgentStageSelection;

export const pcmpUrgentStageResultSchema = z.object({
  batchId: z.coerce.number(),
  batchNo: z.number(),
  isNewBatch: z.boolean(),
  staged: z.array(z.coerce.number()),
  skipped: z.array(pcmpSkippedProposalSchema),
});

export type PcmpUrgentStageResult = z.infer<typeof pcmpUrgentStageResultSchema>;

export const pcmpUrgentPublishResultSchema = z.object({
  batchId: z.coerce.number(),
  batchNo: z.number(),
  /** Routed to Accounts with the batch. */
  published: z.array(z.coerce.number()),
  /** Rejected or returned since staging: published with the batch, never routed. */
  closed: z.array(z.coerce.number()),
});

export type PcmpUrgentPublishResult = z.infer<typeof pcmpUrgentPublishResultSchema>;

export type PcmpUrgentRollbackPayload =
  | ({ comment?: string | undefined } & ({ proposalIds: number[] } | { wholeBatch: true }))
  | {
      proposalIds: number[];
      paymentSource: PcmpChannel;
      comment: string;
    };

export const pcmpUrgentRollbackResultSchema = z.object({
  batchId: z.coerce.number(),
  batchNo: z.number(),
  rolledBack: z.array(z.coerce.number()),
  blocked: z.array(pcmpSkippedProposalSchema).optional().default([]),
  paymentSource: z.enum(PCMP_CHANNELS).optional(),
});

export type PcmpUrgentRollbackResult = z.infer<typeof pcmpUrgentRollbackResultSchema>;

/** The CMP disbursement report of an urgent batch. */
export const pcmpUrgentBatchReportSchema = pcmpCmpReportSchema.extend({
  batch: pcmpUrgentBatchHeaderSchema,
});

export type PcmpUrgentBatchReport = z.infer<typeof pcmpUrgentBatchReportSchema>;

/**
 * One day of Urgent Requests (API LB-1201, legacy LDS-772). count, amount and
 * the totals cover the whole day across pages; firstIndex and rowCount are its
 * slice of the page's items. continued: the day began on an earlier page,
 * which showed its summary.
 */
export const pcmpUrgentRequestDaySchema = z.object({
  /** YYYY-MM-DD, Asia/Dhaka. */
  date: z.string(),
  isToday: z.boolean(),
  count: z.number(),
  amount: z.number(),
  firstIndex: z.number(),
  rowCount: z.number(),
  continued: z.boolean(),
  divisionTotals: z.array(pcmpDivisionTotalSchema),
  regionTotals: z.array(pcmpRegionTotalSchema),
});

export type PcmpUrgentRequestDay = z.infer<typeof pcmpUrgentRequestDaySchema>;

/** Every Urgent Payment request the viewer raised, any status, newest first. */
export const pcmpUrgentRequestsSchema = z.object({
  totalAmount: z.number(),
  /** The days this page's rows fall on, newest first. */
  days: z.array(pcmpUrgentRequestDaySchema),
  requests: z.object({ items: z.array(pcmpRequestMineItemSchema), meta: pcmpPageMetaSchema }),
});

export type PcmpUrgentRequests = z.infer<typeof pcmpUrgentRequestsSchema>;
